import {
  Box,
  Stack,
  Theme,
  IconButton,
  Modal as MuiModal,
} from "@mui/material";
import * as yup from "yup";
import { Close } from "src/assets";
import Text from "../Displays/Text";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import CheckboxInput from "../Inputs/CheckboxInput";
import PrimaryButton from "../Shared/PrimaryButton";
import { yupResolver } from "@hookform/resolvers/yup";

interface SmaCompatibilityCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export interface SmaCompatibilityCheckFormValues {
  checkbox1: boolean;
  checkbox2: boolean;
}

const SmaCompatibilityCheckModal = ({
  onClose,
  isOpen,
  onSubmit,
}: SmaCompatibilityCheckModalProps) => {
  const smaCompatibilityCheckFormSchema: yup.ObjectSchema<SmaCompatibilityCheckFormValues> =
    yup.object({
      checkbox1: yup
        .boolean()
        .required()
        .test(
          "checkbox1-check",
          "page.contractSummary.error",
          function (value) {
            return value === true;
          },
        ),
      checkbox2: yup
        .boolean()
        .required()
        .test(
          "checkbox2-check",
          "page.contractSummary.error",
          function (value) {
            return value === true;
          },
        ),
    });

  const form = useForm<SmaCompatibilityCheckFormValues>({
    mode: "onSubmit",
    defaultValues: {
      checkbox1: false,
      checkbox2: false,
    },
    resolver: yupResolver(smaCompatibilityCheckFormSchema),
  });

  const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name === "checkbox1" || name === "checkbox2") {
        if (
          form.getValues().checkbox1 === true &&
          form.getValues().checkbox2 === true
        ) {
          setFormSubmitDisabled(false);
        } else {
          setFormSubmitDisabled(true);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <MuiModal
      open={isOpen}
      disableAutoFocus
      onClose={onClose}
      aria-labelledby="title"
      aria-describedby="description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack
        gap={6}
        sx={{
          m: 4,
          px: 4,
          py: 5,
          bgcolor: "#fff",
          width: "600px",
          minWidth: "285px",
          borderRadius: "14px",
        }}
      >
        <Stack gap={7}>
          <Stack gap={2}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Text
                text={"Kompatibilität"}
                sx={{
                  fontSize: "22px",
                  fontWeight: "295",
                  lineHeight: "26px",
                  fontFamily: "isonType",
                }}
              />
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                  color: theme.palette.grey[500],
                })}
              >
                <Close />
              </IconButton>
            </Stack>

            <Box
              sx={(theme: Theme) => ({
                color: "#222",
                fontSize: "16px",
                fontWeight: "400",
                lineHeight: "22px",
              })}
            >
              Um dein Einsparpotential zu berechnen, müssen wir die
              Kompatibilität deiner Anlage prüfen.
            </Box>
          </Stack>

          <Stack gap={3}>
            <Stack>
              <Box
                sx={(theme: Theme) => ({
                  color: "#222",
                  fontSize: "13px",
                  fontWeight: "700",
                  lineHeight: "18px",
                })}
              >
                SHM 2.0
              </Box>
              <CheckboxInput
                control={form.control}
                label={
                  <Box
                    sx={(theme: Theme) => ({
                      color: "#222",
                      fontSize: "16px",
                      fontWeight: "400",
                      lineHeight: "22px",
                    })}
                  >
                    Ja, bei mir ist der Sunny Home Manager 2.0 verbaut
                  </Box>
                }
                name="checkbox1"
              />
            </Stack>

            <Stack>
              <Box
                sx={(theme: Theme) => ({
                  color: "#222",
                  fontSize: "13px",
                  fontWeight: "700",
                  lineHeight: "18px",
                })}
              >
                Hardware
              </Box>
              <CheckboxInput
                control={form.control}
                label={
                  <Box
                    sx={(theme: Theme) => ({
                      color: "#222",
                      fontSize: "16px",
                      fontWeight: "400",
                      lineHeight: "22px",
                    })}
                  >
                    Nein, die folgende Hardware ist bei mir nicht verbaut: Sunny
                    Boy Smart Energy SB 3600SE-10 und SB 5000SE-10 & Sunny
                    Island SI6.0H, SI8.0H, SI3.0M, SI4.4M
                  </Box>
                }
                name="checkbox2"
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack gap={4} flexDirection="row" flexWrap="wrap">
          <PrimaryButton
            fullWidth
            sx={{
              textTransform: "none",
            }}
            onClick={onSubmit}
            text={"Weiter"}
            disabled={formSubmitDisabled}
          />
        </Stack>
      </Stack>
    </MuiModal>
  );
};

export default SmaCompatibilityCheckModal;
